/**
 * Turning a logo as the API returns it into something an <img> can load.
 *
 * The API stores a logo as a file and hands it back as bare base64 with no
 * media type — routers/customers.py writes the upload to images/logos/ and the
 * read path returns base64.b64encode(f.read()). The type is recorded nowhere
 * in the payload, so every caller invented the same prefix,
 * `data:image/png;base64,`, which is right for most uploads and wrong for an
 * SVG: the browser decodes the bytes as PNG, fails, and renders nothing. The
 * Add/Edit sidebar appeared to handle SVG only because its *preview* shows
 * FileReader's own data URL, which carries the real type; the moment the same
 * logo came back from the API it broke there too.
 *
 * So the type is recovered from the bytes. Raster formats start with magic
 * numbers and SVG is text, which makes this unambiguous — and it is the same
 * check `file(1)` makes, not a guess from the filename.
 */

// Leading bytes, as base64 would encode them at offset 0. Compared against the
// decoded bytes rather than the base64 text, because base64 only aligns to a
// predictable prefix on 3-byte boundaries.
const SIGNATURES = [
  { mime: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47] },
  { mime: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
  { mime: 'image/gif', bytes: [0x47, 0x49, 0x46, 0x38] },
  { mime: 'image/webp', bytes: [0x52, 0x49, 0x46, 0x46] }, // "RIFF"; WEBP at offset 8
  { mime: 'image/bmp', bytes: [0x42, 0x4d] },
];

function sniffMime(base64Data) {
  let head;
  try {
    // 32 bytes is past every signature above and past the whitespace, XML
    // declaration or comment an SVG may open with.
    head = atob(base64Data.slice(0, 64)).slice(0, 32);
  } catch {
    return 'image/png'; // not decodable; the old behaviour, which at least tries
  }

  const bytes = Array.from(head, (c) => c.charCodeAt(0) & 0xff);
  const match = SIGNATURES.find((s) => s.bytes.every((b, i) => bytes[i] === b));
  if (match) return match.mime;

  // Text that opens a tag is SVG. `<?xml` and `<!--` count: an SVG exported by
  // Illustrator or Inkscape leads with one or the other far more often than
  // with <svg itself.
  if (/^\s*<(\?xml|!--|svg|!DOCTYPE svg)/i.test(head)) return 'image/svg+xml';

  return 'image/png';
}

/**
 * An <img src> for a logo, whether the value is already a data URL (fresh from
 * a file picker) or bare base64 (from the API). Returns '' for nothing, so it
 * can be used in a src without guarding.
 */
export function imageSrc(value) {
  if (!value) return '';
  const data = typeof value === 'string' ? value : String(value);
  if (data.startsWith('data:')) return data;
  return `data:${sniffMime(data)};base64,${data}`;
}

export default imageSrc;
